import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAuditById, saveChecklistResponses } from '../../services/auditAPI';
import { setLoading, setAuditDetails, setError } from '../../redux/slices/auditSlice';
import { Button, Form, Radio, Spin, message, Input } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';

const AuditDetailPage = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { auditDetails, loading, error } = useSelector((state) => state.audit);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAuditDetails();
  }, [auditId]);

  const fetchAuditDetails = async () => {
    dispatch(setLoading(true));
    try {
      const audit = await getAuditById(auditId);

      // Ensure checklist items have consistent unique ids for form handling
      const checklistWithResponses = audit.checklist.map((item, index) => {
        const uid = item._id || item.id || `temp-${index}`;
        const saved = audit.checklistResponses?.find(
          (res) => res.checklistItemId?.toString() === uid.toString()
        );

        return {
          ...item,
          uid,
          response: saved?.response || undefined,
        };
      });

      const mergedAudit = {
        ...audit,
        checklist: checklistWithResponses,
      };

      dispatch(setAuditDetails(mergedAudit));

      // Prepare form default values
      const initialFormValues = {};
      checklistWithResponses.forEach((item) => {
        if (item.response) {
          initialFormValues[`checklistItem_${item.uid}`] = item.response;
        }
      });
      if (audit.checklistRemarks) {
        initialFormValues.remarks = audit.checklistRemarks;
      }

      // console.log('Initial Form Values:', initialFormValues);
      form.setFieldsValue(initialFormValues);
    } catch (err) {
      dispatch(setError(err.message || 'Failed to fetch audit details.'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleRadioChange = async (value, questionId) => {
    try {
      const payload = {
        checklistResponses: [{ checklistItemId: questionId, response: value }],
        checklistRemarks: form.getFieldValue('remarks') || '',
      };
      await saveChecklistResponses(auditId, payload);
      message.success('Response saved!');
    } catch (err) {
      console.error(err);
      message.error('Failed to save response.');
    }
  };

  const handleSaveChecklist = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const checklistResponses = Object.entries(values)
        .filter(([key]) => key.startsWith('checklistItem_'))
        .map(([key, response]) => ({
          checklistItemId: key.replace('checklistItem_', ''),
          response,
        }));

      const checklistRemarks = values.remarks || '';

      await saveChecklistResponses(auditId, { checklistResponses, checklistRemarks });

      message.success('Checklist responses saved successfully!');
    } catch (err) {
      console.error(err);
      message.error('Failed to save checklist responses.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateReport = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const checklistResponses = Object.entries(values)
        .filter(([key]) => key.startsWith('checklistItem_'))
        .map(([key, response]) => ({
          checklistItemId: key.replace('checklistItem_', ''),
          response,
        }));

      const checklistRemarks = values.remarks || '';

      await saveChecklistResponses(auditId, { checklistResponses, checklistRemarks });

      message.success('Checklist saved! Proceeding to create report...');
      navigate(`/user/audit/${auditId}/create-report`);
    } catch (err) {
      console.error(err);
      message.error('Please complete the checklist before proceeding.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 text-red-700 p-4 rounded-md m-4">
        <strong>Error:</strong> {error}
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">{auditDetails?.title || 'Audit Checklist'}</h2>

      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changedValues) => {
          const [changedKey] = Object.keys(changedValues);
          const changedValue = changedValues[changedKey];
          if (changedKey.startsWith('checklistItem_')) {
            const checklistItemId = changedKey.replace('checklistItem_', '');
            handleRadioChange(changedValue, checklistItemId);
          }
        }}
      >
        {auditDetails?.checklist?.map((item, index) => (
          <Form.Item
            key={item.uid}
            label={`${index + 1}. ${item.question}`}
            name={`checklistItem_${item.uid}`}
            rules={[{ required: true, message: 'Please select an option' }]}
          >
            <Radio.Group>
              <Radio value="Yes">Yes</Radio>
              <Radio value="No">No</Radio>
            </Radio.Group>
          </Form.Item>
        ))}

        <Form.Item
          label="Remarks (optional)"
          name="remarks"
        >
          <Input.TextArea placeholder="Add any remarks here" />
        </Form.Item>

        <div className="flex flex-col gap-4 mt-8">
          <Button
            type="primary"
            onClick={handleSaveChecklist}
            loading={saving}
            className="w-full"
          >
            Save Checklist
          </Button>

          {/* <Button
            type="dashed"
            onClick={handleCreateReport}
            loading={saving}
            className="w-full"
          >
            Create Report
          </Button> */}
        </div>
      </Form>
    </div>
  );
};

export default AuditDetailPage;
